import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ShoppingCart,
  Heart,
  Share2,
  Star,
  Minus,
  Plus,
  Truck,
  Shield,
  RotateCcw
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import useApiProduct from '../services/apiProduct'
import { useCart } from '../context/CartContext';

const ProductDetail = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const [selectedTalla, setSelectedTalla] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedMedida, setSelectedMedida] = useState(null);

  const { data: product, loading, error } = useApi(() => useApiProduct.getProduct(slug), []);
  console.log(product);

  const handleAddToCart = async () => {
    let variedadId = null

    if (product.variedades.length > 0) {
      const variedad = product.variedades.find(variant =>
        variant.talla === selectedTalla && variant.color === selectedColor && variant.medida === selectedMedida
      );

      variedadId = variedad.id;
    }


    try {
      console.log(variedadId);
      await addToCart(product, quantity, variedadId);
      // Mostrar notificación de éxito
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="animate-pulse">
              <div className="bg-gray-200 h-96 rounded-2xl mb-4"></div>
              <div className="flex gap-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-gray-200 h-20 w-20 rounded-lg"></div>
                ))}
              </div>
            </div>
            <div className="animate-pulse">
              <div className="bg-gray-200 h-8 rounded mb-4"></div>
              <div className="bg-gray-200 h-6 rounded mb-6"></div>
              <div className="bg-gray-200 h-12 rounded mb-6"></div>
              <div className="bg-gray-200 h-32 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Producto no encontrado</h2>
          <p className="text-gray-600 mb-6">{error || 'El producto que buscas no existe'}</p>
          <button
            onClick={() => navigate('/products')}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
          >
            Volver a productos
          </button>
        </div>
      </div>
    );
  }


  const images = [
    `${API_BASE_URL}/producto/obtener_image_producto/` + product.portada.split('/').pop(),
    ...(product.galerias?.map(galeria =>
      `${API_BASE_URL}/producto/obtener_galeria_producto/` + galeria.image
    ) || [])
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto p-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Galería de imágenes */}
          <div className="space-y-4">
            <motion.div
              className="relative bg-white rounded-2xl overflow-hidden shadow-sm"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={images[selectedImage] || '/api/placeholder/600/600'}
                alt={product.name}
              />
              <button className="absolute top-4 right-4 p-3 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                <Heart className="w-5 h-5 text-gray-600" />
              </button>
              <button className="absolute top-4 right-16 p-3 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
            </motion.div>

            {/* Miniaturas */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${selectedImage === index ? 'border-blue-500' : 'border-gray-200'
                      }`}
                  >
                    <img
                      src={image || '/api/placeholder/80/80'}
                      alt={`${product.name} ${index + 1}`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Información del producto */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {product.titulo}
              </h1>

              {product.rating && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < Math.floor(product.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                          }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium">{product.rating}</span>
                  <span className="text-sm text-gray-500">
                    ({product.reviewCount} reseñas)
                  </span>
                </div>
              )}

              <div className="flex items-center gap-4 mb-6">
                <span className="text-3xl font-bold text-gray-900">
                  {formatPrice(product.precio)}
                </span>
                {product.originalPrice && (
                  <span className="text-xl text-gray-500 line-through">
                    {formatPrice(product.precio)}
                  </span>
                )}
                {product.discount && (
                  <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                    -{product.discount}% OFF
                  </span>
                )}
              </div>
            </motion.div>


            <div className="space-y-6">
              {/* Tallas disponibles */}
              {product.talla && (
                <motion.div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Talla:</h3>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(product.variedades.map(v => v.talla).filter(Boolean))].map((talla) => (
                      <button
                        key={talla}
                        onClick={() => setSelectedTalla(talla)}
                        className={`px-4 py-2 rounded-lg border transition-colors ${selectedTalla === talla
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        {talla}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}


              {/* Colores disponibles */}
              {product.color && (
                <motion.div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Color:</h3>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(product.variedades.map(v => v.color).filter(Boolean))].map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`px-4 py-2 rounded-lg border transition-colors ${selectedColor === color
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Mostrar combinación seleccionada */}
              {selectedTalla && selectedColor && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800">
                    Has seleccionado: Talla {selectedTalla}, Color {selectedColor}
                  </p>
                </div>
              )}
            </div>

            {
              /*
            {product.talla && product.variedades.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="space-y-4"
              >
                <h3 className="font-semibold text-gray-900">Tallas:</h3>
                <div className="flex flex-wrap gap-2">
                  {product.variedades
                    .filter(variant => variant.talla && variant.talla.trim() !== '')
                    .filter((variant, index, array) =>
                      array.findIndex(v => v.talla === variant.talla) === index
                    ) // Eliminar duplicados
                    .map((variant) => (
                      <button
                        key={`talla-${variant.talla}`}
                        onClick={() => setSelectedVariant(variant)}
                        className={`px-4 py-2 rounded-lg border transition-colors ${selectedVariant?.talla === variant.talla
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        {variant.talla}
                      </button>
                    ))}
                </div>
              </motion.div>
            )}

            {product.color && product.variedades.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="space-y-4"
              >
                <h3 className="font-semibold text-gray-900">Colores:</h3>
                <div className="flex flex-wrap gap-2">
                  {product.variedades
                    .filter(variant => variant.color && variant.color.trim() !== '')
                    .filter((variant, index, array) =>
                      array.findIndex(v => v.color === variant.color) === index
                    ) // Eliminar duplicados
                    .map((variant) => (
                      <button
                        key={`color-${variant.color}`}
                        onClick={() => setSelectedVariant(variant)}
                        className={`px-4 py-2 rounded-lg border transition-colors ${selectedVariant?.color === variant.color
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        {variant.color}
                      </button>
                    ))}
                </div>
              </motion.div>
            )}
            */
            }
            {/* Cantidad */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="space-y-4"
            >
              <h3 className="font-semibold text-gray-900">Cantidad:</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-50 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-3 font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-sm text-gray-500">
                  {product.stock} disponibles
                </span>
              </div>
            </motion.div>

            {/* Botones de acción */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="space-y-4"
            >
              <button
                onClick={handleAddToCart}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Agregar al Carrito
              </button>

              <button className="w-full border border-gray-200 hover:border-gray-300 text-gray-900 px-6 py-4 rounded-xl font-semibold transition-colors">
                Comprar Ahora
              </button>
            </motion.div>

            {/* Información adicional */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="space-y-4 pt-6 border-t border-gray-200"
            >
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-600">Envío gratis en pedidos superiores a 50€</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-600">Garantía de 2 años</span>
              </div>
              <div className="flex items-center gap-3">
                <RotateCcw className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-gray-600">Devoluciones gratuitas en 30 días</span>
              </div>
            </motion.div>

            {/* Descripción */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="space-y-4 pt-6 border-t border-gray-200"
            >
              <h3 className="font-semibold text-gray-900">Descripción:</h3>
              <p className="text-gray-600 leading-relaxed">
                {product.extracto}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;